import { useCallback, useContext, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { AuthContext } from 'src/context/auth.context'
import { ActivityLogEntityType } from 'src/domain/activityLog'
import { ApiResponse } from 'src/domain/api'
import { INFINITE_SCROLL_CONTENT_LOADING_NUM } from 'src/domain/config'
import {
  ActivityLog,
  ActivityLogEntityTypeEnum,
  ActivityLogScopeTypeEnum,
} from 'src/slices/services/api'
import AuthenticatedApi from 'src/slices/services/authenticatedApi'
import { axiosErrorMessage } from 'src/slices/services/error'

const entityTypeMap: Record<ActivityLogEntityType, ActivityLogEntityTypeEnum> =
  {
    [ActivityLogEntityType.Tenant]: ActivityLogEntityTypeEnum.Tenant,
    [ActivityLogEntityType.Member]: ActivityLogEntityTypeEnum.Member,
    [ActivityLogEntityType.Calculation]:
      ActivityLogEntityTypeEnum.CalculationDsl,
    [ActivityLogEntityType.SalesReport]:
      ActivityLogEntityTypeEnum.CalculationDefiniteValue,
  }

type ActivityLogFilters = {
  scopeType?: ActivityLogScopeTypeEnum
  scopeId?: string
  from?: string
  to?: string
}

const getDateRange = (
  filters?: ActivityLogFilters
): { from: string; to: string } => ({
  from: filters?.from || dayjs().subtract(1, 'month').toISOString(),
  to: filters?.to || dayjs().toISOString(),
})

export const useApiActivityLogs = () => {
  const authContext = useContext(AuthContext)
  const authContextRef = useRef(authContext)
  authContextRef.current = authContext
  const [logs, setLogs] = useState<ActivityLog[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const currentCursorRef = useRef<string | null>(null)
  const currentParamsRef = useRef<{
    orgCode: string
    entityType: ActivityLogEntityType
    filters?: ActivityLogFilters
  } | null>(null)
  // 日付範囲の高速切替で起きる race を世代番号で破棄する
  const generationRef = useRef(0)

  const fetchActivityLogs = useCallback(
    async (
      orgCode: string,
      entityType: ActivityLogEntityType,
      filters: ActivityLogFilters | undefined,
      from: string,
      to: string,
      limit: number,
      cursor: string | null
    ): Promise<
      ApiResponse<{ logs: ActivityLog[]; nextCursor: string | null }>
    > => {
      const token = authContextRef.current.getAuthToken()
      if (!token) {
        return {
          success: false,
          message: 'ログインしなおしてください: token is not found',
        }
      }

      const apiEntityType = entityTypeMap[entityType]
      if (!apiEntityType) {
        return {
          success: false,
          message: `未対応のentityTypeです: ${entityType}`,
        }
      }

      if (!filters?.scopeType || !filters?.scopeId) {
        return { success: false, message: 'scopeTypeとscopeIdは必須です' }
      }

      const { scopeType, scopeId } = filters as {
        scopeType: ActivityLogScopeTypeEnum
        scopeId: string
      }

      const res = await new AuthenticatedApi(
        token
      ).getOrganizationsOrganizationCodeActivityLogsEntityType(
        orgCode,
        apiEntityType,
        from,
        to,
        scopeType,
        scopeId,
        limit,
        cursor ?? undefined
      )
      return {
        success: true,
        data: { logs: res.data.items, nextCursor: res.data.nextCursor },
      }
    },
    []
  )

  // 共通のフェッチ実行関数
  const executeFetch = useCallback(
    async (
      params: {
        orgCode: string
        entityType: ActivityLogEntityType
        filters?: ActivityLogFilters
      },
      cursor: string | null
    ): Promise<
      ApiResponse<{ logs: ActivityLog[]; nextCursor: string | null }>
    > => {
      const { orgCode, entityType, filters } = params
      const { from, to } = getDateRange(filters)

      return fetchActivityLogs(
        orgCode,
        entityType,
        filters,
        from,
        to,
        INFINITE_SCROLL_CONTENT_LOADING_NUM,
        cursor
      )
    },
    [fetchActivityLogs]
  )

  // 結果の状態更新関数
  const updateStateFromResult = useCallback(
    (
      result: { logs: ActivityLog[]; nextCursor: string | null },
      isAppend: boolean
    ) => {
      const { logs: newLogs, nextCursor } = result
      currentCursorRef.current = nextCursor
      setHasMore(nextCursor !== null && nextCursor !== '')
      if (isAppend) {
        setLogs((prev) => [...prev, ...newLogs])
      } else {
        setLogs(newLogs)
      }
    },
    []
  )

  const getActivityLogs = useCallback(
    async (
      orgCode: string,
      entityType: ActivityLogEntityType,
      filters?: ActivityLogFilters
    ): Promise<ApiResponse<ActivityLog[]>> => {
      generationRef.current += 1
      const generation = generationRef.current
      setIsLoading(true)
      setLogs([])
      setHasMore(false)
      currentCursorRef.current = null

      const params = { orgCode, entityType, filters }
      currentParamsRef.current = params

      // stale (より新しいリクエストで上書き済) は no-op を返す。
      // success: true だが logs は更新せず、上位の cancelled でも破棄される前提
      const staleResult: ApiResponse<ActivityLog[]> = {
        success: true,
        data: [],
      }
      try {
        const result = await executeFetch(params, null)
        if (generation !== generationRef.current) return staleResult

        if (result.success && result.data) {
          updateStateFromResult(result.data, false)
          return { success: true, data: result.data.logs }
        }
        return {
          success: false,
          message: result.message || 'データの取得に失敗しました',
        }
      } catch (e: unknown) {
        if (generation !== generationRef.current) return staleResult
        const message = axiosErrorMessage(e)
        return { success: false, message }
      } finally {
        if (generation === generationRef.current) {
          setIsLoading(false)
        }
      }
    },
    [executeFetch, updateStateFromResult]
  )

  const loadMore = useCallback(async (): Promise<ApiResponse<void>> => {
    if (!currentParamsRef.current || isLoadingMore || !hasMore) {
      return { success: true }
    }

    const generation = generationRef.current
    // stale (日付範囲が変わった等) の場合はリスト更新せず caller に no-op を返す
    const staleResult: ApiResponse<void> = { success: true }
    try {
      setIsLoadingMore(true)

      const result = await executeFetch(
        currentParamsRef.current,
        currentCursorRef.current
      )

      if (generation !== generationRef.current) return staleResult

      if (result.success && result.data) {
        updateStateFromResult(result.data, true)
        return { success: true }
      }
      setHasMore(false)
      return {
        success: false,
        message: result.message || '追加データの取得に失敗しました',
      }
    } catch (e: unknown) {
      if (generation !== generationRef.current) return staleResult
      setHasMore(false)
      const message = axiosErrorMessage(e)
      return { success: false, message }
    } finally {
      // isLoadingMore は loadMore 専用の状態なので stale でも必ず解除する。
      setIsLoadingMore(false)
    }
  }, [executeFetch, updateStateFromResult, isLoadingMore, hasMore])

  const clearLogs = useCallback(() => {
    generationRef.current += 1
    setIsLoading(false)
    setIsLoadingMore(false)
    setLogs([])
    setHasMore(false)
    currentCursorRef.current = null
    currentParamsRef.current = null
  }, [])

  return {
    logs,
    isLoading,
    isLoadingMore,
    hasMore,
    getActivityLogs,
    loadMore,
    clearLogs,
  }
}
